/**
 * Client-side PDF generation for certificates
 * Uses html-to-image + jsPDF for WYSIWYG output.
 *
 * CRITICAL: The PDF must be pixel-identical to the HTML preview.
 * To achieve this:
 * 1. The CertificatePreview component renders at FIXED dimensions:
 *    1123px x 794px (A4 landscape at 96 DPI)
 * 2. Fonts are ABSOLUTE in px (not rem-based)
 * 3. We capture the inner element at its native resolution, stripping
 *    any CSS transform:scale() that may have been applied for display
 * 4. The captured image is placed into the PDF at exact A4 dimensions
 *
 * This guarantees: what the user sees = what gets printed. Period.
 */

import { toPng } from "html-to-image";
import jsPDF from "jspdf";

/**
 * A4 Landscape dimensions at 96 DPI (standard web resolution)
 * Must match CertificatePreview exactly.
 */
const CERTIFICATE_WIDTH = 1123;
const CERTIFICATE_HEIGHT = 794;

/**
 * A4 landscape dimensions in PDF points (72 DPI)
 */
const A4_PT = {
  width: 841.89,
  height: 595.28,
};

async function waitForFonts(): Promise<void> {
  if ("fonts" in document) {
    await (document).fonts?.ready;
  }
}

async function waitForImages(element: HTMLElement): Promise<void> {
  const images = element.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map(
      (img) =>
        new Promise<void>((resolve, reject) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
          } else if (img.complete && img.naturalWidth === 0) {
            reject(new Error(`Falha ao carregar imagem: ${img.src}`));
          } else {
            img.onload = () => {
              if (img.naturalWidth > 0) resolve();
              else reject(new Error(`Falha ao carregar imagem: ${img.src}`));
            };
            img.onerror = () => reject(new Error(`Falha ao carregar imagem: ${img.src}`));
          }
        })
    )
  );
}

/**
 * Generate professional PDF from certificate preview element.
 *
 * The previewElement MUST be the inner certificate-preview div
 * (the one with data-testid="certificate-preview"), NOT the wrapper.
 * It must have fixed dimensions of CERTIFICATE_WIDTH x CERTIFICATE_HEIGHT px.
 */
export async function generateCertificatePDF(
  previewElement: HTMLElement,
  filename: string = "certificado.pdf"
): Promise<void> {
  await waitForFonts();
  await waitForImages(previewElement);

  // Ensure the element has its native fixed dimensions during capture.
  const originalTransform = previewElement.style.transform;
  const originalTransformOrigin = previewElement.style.transformOrigin;
  const originalWidth = previewElement.style.width;
  const originalHeight = previewElement.style.height;
  const originalMaxWidth = previewElement.style.maxWidth;

  previewElement.style.transform = "none";
  previewElement.style.transformOrigin = "top left";
  previewElement.style.width = `${CERTIFICATE_WIDTH}px`;
  previewElement.style.height = `${CERTIFICATE_HEIGHT}px`;
  previewElement.style.maxWidth = "none";

  // Give extra time for any CSS transitions/layouts to settle
  await new Promise((resolve) => setTimeout(resolve, 300));

  try {
    const dataUrl = await toPng(previewElement, {
      pixelRatio: 3,
      width: CERTIFICATE_WIDTH,
      height: CERTIFICATE_HEIGHT,
      cacheBust: true,
      skipFonts: false,
    });

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    const pageWidth = A4_PT.width;
    const pageHeight = A4_PT.height;

    pdf.addImage(
      dataUrl,
      "PNG",
      0,
      0,
      pageWidth,
      pageHeight,
      undefined,
      "MEDIUM"
    );

    pdf.save(filename);
  } finally {
    previewElement.style.transform = originalTransform;
    previewElement.style.transformOrigin = originalTransformOrigin;
    previewElement.style.width = originalWidth;
    previewElement.style.height = originalHeight;
    previewElement.style.maxWidth = originalMaxWidth;
  }
}

/**
 * Generate PNG from certificate preview element.
 */
export async function generateCertificatePNG(
  previewElement: HTMLElement,
  filename: string = "certificado.png"
): Promise<void> {
  await waitForFonts();
  await waitForImages(previewElement);

  const dataUrl = await toPng(previewElement, {
    pixelRatio: 3,
    width: CERTIFICATE_WIDTH,
    height: CERTIFICATE_HEIGHT,
    cacheBust: true,
    skipFonts: false,
  });

  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
